import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveData, Route, Router, UrlTree } from '@angular/router';
import { copyDefaults, resolveContent } from '@bfy/bfy-ui-shared';
import { Observable, of } from 'rxjs';
import {
    CatchAllRouteData,
    HeaderStyle,
    RootRoute,
    RootRouteData,
    RootRouteProperties,
} from '../constants/root-routes';

export const getRootComponentRoute = (routeData: Partial<RootRoute>): Route => {
    const output: Route = {};
    Object.assign(output, routeData);

    // remove any properties defined in RootRoute from main part of object
    for (const property of RootRouteProperties) {
        delete (output as any)[property];
    }

    const newData: RootRouteData = {};
    Object.assign(newData, routeData);

    // remove any properties not defined in ParticipantRoute from data property of object
    for (const key in newData) {
        if (!RootRouteProperties.includes(key)) {
            delete (newData as any)[key];
        }
    }

    const newResolve: ResolveData = {};

    // automatically add the content resolver given the page content to load
    if (routeData.page) {
        newData.page = routeData.page;
        newResolve['content'] = (route: ActivatedRouteSnapshot) => resolveContent(route);
    }

    copyDefaults(newData, {
        headerStyle: HeaderStyle.Standard,
    });

    output.data = {
        ...output.data,
        ...newData,
    };

    output.resolve = {
        ...output.resolve,
        ...newResolve,
    };

    return output;
};

export const getCatchAllRoute = (routeData: Partial<Route>): Route => {
    const output: Route = {};
    Object.assign(output, routeData);

    // set the default path to the wildcard route
    output.path = '**';

    output.children = [];
    output.redirectTo = undefined;

    // automatically add the catch all guard
    output.canActivate = [
        ...(output.canActivate || []),
        (route: ActivatedRouteSnapshot) => handleCatchAllRoute(route),
    ];

    // set the redirectTo URL for use in the auth guard
    const newData: CatchAllRouteData = {
        redirectTo: routeData.redirectTo ?? '',
    };

    output.data = {
        ...output.data,
        ...newData,
    };

    return output;
};

const handleCatchAllRoute = (route: ActivatedRouteSnapshot): Observable<UrlTree> => {
    // load up the redirect information from the standard routing
    const data: CatchAllRouteData = route.data as CatchAllRouteData;

    let redirectTo = data.redirectTo as string;
    if (redirectTo.includes(':')) {
        const redirectToSegments = redirectTo.split('/');
        for (let idx = redirectToSegments.length - 1; idx >= 0; idx--) {
            const segment = redirectToSegments[idx];
            if (segment.startsWith(':')) {
                const paramVal = route.paramMap?.get(segment.slice(1));
                if (paramVal != null) {
                    redirectToSegments[idx] = paramVal;
                }
            }
        }

        redirectTo = redirectToSegments.join('/');
    }

    return of(inject(Router).parseUrl(redirectTo));
};
